import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Award, Lightbulb, Users, Shield, Clock, Target } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function About() {
  const { t } = useLanguage();

  const values = [
    { icon: Award, ...t('about.values.excellence') },
    { icon: Lightbulb, ...t('about.values.innovation') },
    { icon: Users, ...t('about.values.partnership') },
    { icon: Shield, ...t('about.values.trust') },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-[#00D4FF]/20 text-[#00D4FF] rounded-full text-sm font-medium mb-6">
              7x7 Travel
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('about.title')}
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              {t('about.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-white" data-testid="about-mission">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              className="p-10 bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl"
              {...fadeInUp}
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#00D4FF] flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('about.missionTitle')}
              </h2>
              <p className="text-slate-600 leading-relaxed text-lg">
                {t('about.missionText')}
              </p>
            </motion.div>

            <motion.div
              className="p-10 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] rounded-3xl"
              {...fadeInUp}
              transition={{ delay: 0.1 }}
            >
              <div className="w-16 h-16 rounded-2xl bg-[#00D4FF]/20 flex items-center justify-center mb-6">
                <Lightbulb className="w-8 h-8 text-[#00D4FF]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('about.visionTitle')}
              </h2>
              <p className="text-slate-300 leading-relaxed text-lg">
                {t('about.visionText')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('about.valuesTitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="group p-8 bg-white rounded-2xl border border-slate-100 hover:border-[#00D4FF]/30 hover:shadow-xl transition-all duration-300 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#00D4FF] flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {value.title}
                </h3>
                <p className="text-slate-600">
                  {value.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* History */}
      <section className="py-24 bg-white" data-testid="about-history">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              {...fadeInUp}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('about.historyTitle')}
              </h2>
              <p className="text-slate-600 leading-relaxed text-lg mb-6">
                {t('about.historyText')}
              </p>
              <div className="flex items-center gap-8 mt-8">
                <div>
                  <div className="text-4xl font-bold text-[#1E3A8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>10+</div>
                  <div className="text-slate-600">Anos</div>
                </div>
                <div className="w-px h-16 bg-slate-200" />
                <div>
                  <div className="text-4xl font-bold text-[#1E3A8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>500+</div>
                  <div className="text-slate-600">Parceiros</div>
                </div>
                <div className="w-px h-16 bg-slate-200" />
                <div>
                  <div className="text-4xl font-bold text-[#1E3A8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>6000+</div>
                  <div className="text-slate-600">Pessoas</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="aspect-square rounded-3xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1626707647519-939352c5a37d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxQb3J0dWdhbCUyMFNwYWluJTIwY29hc3QlMjBhZXJpYWwlMjBzdW5ueSUyMG9jZWFufGVufDB8fHx8MTc3MTcxNDI4MHww&ixlib=rb-4.1.0&q=85"
                  alt="7x7 Travel History"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#00D4FF] rounded-2xl -z-10" />
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#1E3A8A] rounded-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
